import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { HelpCircle, X, File, History } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Question, normalizeChoices, AnswerValue } from './questionnaireTypes';
import FileDropZone from '../../components/common/FileDropZone';

interface QuestionRendererProps {
  question: Question;
  value: AnswerValue | undefined;
  isReadOnly: boolean;
  uploadedFile?: { name: string; path: string };
  isUploading?: boolean;
  isCarriedForward?: boolean;
  carriedFromLabel?: string;
  onAnswerChange: (questionId: number, value: string | number | boolean | string[] | Record<string, string | string[]>) => void;
  onFileUpload: (questionId: number, file: File) => void;
  onFileRemove: (questionId: number) => void;
}

export function QuestionRenderer({
  question,
  value,
  isReadOnly,
  uploadedFile,
  isUploading,
  isCarriedForward,
  carriedFromLabel,
  onAnswerChange,
  onFileUpload,
  onFileRemove,
}: QuestionRendererProps) {
  return (
    <div
      key={question.id}
      className={`space-y-2 p-4 border rounded-lg bg-background ${isCarriedForward ? 'border-amber-300 bg-amber-50/40' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <Label className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
          {question.question_text}
          {question.is_required && <span className="text-red-500">*</span>}
          {isCarriedForward && (
            <Badge
              variant="outline"
              className="gap-1 border-amber-300 bg-amber-50 text-amber-800 text-[10px] font-normal"
              title={carriedFromLabel ? `Copied from ${carriedFromLabel} — confirm or update.` : 'Copied from your previous submission — confirm or update.'}
            >
              <History className="h-3 w-3" />
              Prefilled{carriedFromLabel ? ` from ${carriedFromLabel}` : ' from last submission'}
            </Badge>
          )}
        </Label>
        {question.help_text && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <HelpCircle className="h-3 w-3" />
            {question.help_text}
          </span>
        )}
      </div>

      {/* Text Input */}
      {(question.question_type === 'text' || question.question_type === 'email' ||
        question.question_type === 'phone' || question.question_type === 'url') && (
        <Input
          type={question.question_type === 'email' ? 'email' :
                question.question_type === 'url' ? 'url' : 'text'}
          placeholder={`Enter ${question.question_type === 'email' ? 'email address' :
                       question.question_type === 'phone' ? 'phone number' :
                       question.question_type === 'url' ? 'URL' : 'your answer'}`}
          value={String(value ?? '')}
          onChange={(e) => onAnswerChange(question.id, e.target.value)}
          disabled={isReadOnly}
        />
      )}

      {/* Textarea */}
      {question.question_type === 'textarea' && (
        <Textarea
          placeholder="Enter your answer"
          value={String(value ?? '')}
          onChange={(e) => onAnswerChange(question.id, e.target.value)}
          disabled={isReadOnly}
          rows={4}
        />
      )}

      {/* Number Input */}
      {(question.question_type === 'number' || question.question_type === 'currency' ||
        question.question_type === 'percentage') && (
        <Input
          type="number"
          placeholder={`Enter ${question.question_type === 'currency' ? 'amount' :
                       question.question_type === 'percentage' ? 'percentage' : 'number'}`}
          value={String(value ?? '')}
          onChange={(e) => onAnswerChange(question.id, e.target.value)}
          disabled={isReadOnly}
        />
      )}

      {/* Date Input */}
      {question.question_type === 'date' && (
        <Input
          type="date"
          value={String(value ?? '')}
          onChange={(e) => onAnswerChange(question.id, e.target.value)}
          disabled={isReadOnly}
        />
      )}

      {/* Yes/No */}
      {question.question_type === 'yes_no' && (
        <RadioGroup
          value={value === true ? 'yes' : value === false ? 'no' : ''}
          onValueChange={(v: string) => onAnswerChange(question.id, v === 'yes')}
          disabled={isReadOnly}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${question.id}-yes`} />
            <Label htmlFor={`${question.id}-yes`}>Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${question.id}-no`} />
            <Label htmlFor={`${question.id}-no`}>No</Label>
          </div>
        </RadioGroup>
      )}

      {/* Single Choice */}
      {question.question_type === 'single_choice' && question.choices && (
        <RadioGroup
          value={String(value ?? '')}
          onValueChange={(v: string) => onAnswerChange(question.id, v)}
          disabled={isReadOnly}
          className="space-y-2"
        >
          {normalizeChoices(question.choices).map((choice, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <RadioGroupItem value={choice.value} id={`${question.id}-${idx}`} />
              <Label htmlFor={`${question.id}-${idx}`}>{choice.label}</Label>
            </div>
          ))}
        </RadioGroup>
      )}

      {/* Multiple Choice */}
      {question.question_type === 'multiple_choice' && question.choices && (
        <div className="space-y-2">
          {normalizeChoices(question.choices).map((choice, idx) => {
            const selectedValues = Array.isArray(value) ? value : [];
            const isChecked = selectedValues.includes(choice.value);
            return (
              <div key={idx} className="flex items-center space-x-2">
                <Checkbox
                  id={`${question.id}-${idx}`}
                  checked={isChecked}
                  disabled={isReadOnly}
                  onCheckedChange={(checked) => {
                    const newValues = checked
                      ? [...selectedValues, choice.value]
                      : selectedValues.filter((v: string) => v !== choice.value);
                    onAnswerChange(question.id, newValues);
                  }}
                />
                <Label htmlFor={`${question.id}-${idx}`}>{choice.label}</Label>
              </div>
            );
          })}
        </div>
      )}

      {/* Rating */}
      {question.question_type === 'rating' && (
        <Select
          value={value?.toString() || ''}
          onValueChange={(v) => onAnswerChange(question.id, Number(v))}
          disabled={isReadOnly}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select rating" />
          </SelectTrigger>
          <SelectContent>
            {[1, 2, 3, 4, 5].map((rating) => (
              <SelectItem key={rating} value={rating.toString()}>
                {rating} - {rating === 1 ? 'Poor' : rating === 2 ? 'Fair' :
                           rating === 3 ? 'Good' : rating === 4 ? 'Very Good' : 'Excellent'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* File Upload */}
      {question.question_type === 'file_upload' && (
        <div className="space-y-2">
          {uploadedFile || value ? (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <File className="h-5 w-5 text-blue-500" />
              <span className="flex-1 truncate">
                {uploadedFile?.name || String(value ?? '')}
              </span>
              {!isReadOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onFileRemove(question.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : (
            // Customer review 11 Sep (drag and drop everywhere).
            <FileDropZone
              inputId={`file-${question.id}`}
              disabled={isReadOnly}
              busy={isUploading}
              hint="PDF, DOC, DOCX, XLS, XLSX, PNG, JPG up to 10MB"
              onFiles={(files) => onFileUpload(question.id, files[0])}
              data-testid={`onboarding-question-drop-zone-${question.id}`}
            />
          )}
        </div>
      )}

      {/* Matrix */}
      {question.question_type === 'matrix' && question.matrix_config && (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-3 text-left font-medium min-w-[200px]"></th>
                {question.matrix_config.columns?.map((col) => (
                  <th key={col.id} className="p-3 text-center font-medium min-w-[100px]">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {question.matrix_config.rows?.map((row) => {
                const matrixValue = value as Record<string, string | string[]> | undefined;
                const rowValue = matrixValue?.[row.id];

                return (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="p-3 font-medium">{row.label}</td>
                    {question.matrix_config!.columns?.map((col) => {
                      const isSelected = question.matrix_config!.allowMultiple
                        ? (rowValue as string[] | undefined)?.includes(col.id)
                        : rowValue === col.id;

                      return (
                        <td key={col.id} className="p-3 text-center">
                          <input
                            type={question.matrix_config!.allowMultiple ? 'checkbox' : 'radio'}
                            name={`matrix_${question.id}_${row.id}`}
                            checked={isSelected || false}
                            disabled={isReadOnly}
                            className="h-4 w-4 cursor-pointer disabled:cursor-not-allowed"
                            onChange={(e) => {
                              const currentMatrix = (value as Record<string, string | string[]>) || {};
                              let newRowValue: string | string[];

                              if (question.matrix_config!.allowMultiple) {
                                const currentRowValues = (currentMatrix[row.id] as string[]) || [];
                                if (e.target.checked) {
                                  newRowValue = [...currentRowValues, col.id];
                                } else {
                                  newRowValue = currentRowValues.filter(v => v !== col.id);
                                }
                              } else {
                                newRowValue = col.id;
                              }

                              onAnswerChange(question.id, {
                                ...currentMatrix,
                                [row.id]: newRowValue,
                              });
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
